import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormValidation } from '../../../commons/interfaces/FormValidationCommonInterface';
import { NewAgreementServiceDetail } from '../../../models/Entities/AgreementServices/NewAgreementServiceDetail';
import { AgreementServicesDetail } from '../../../models/Entities/AgreementServices/AgreementServicesDetail';
import {
  Badge,
  Button,
  Divider,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  OverlayDrawer,
  Spinner,
  Switch,
  Textarea
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

////i18Next
import { useTranslation } from 'react-i18next';

// Functions
import * as ownFunctions from './AgreementServiceDetailComponentFunctions';

// Styles
import './AgreementServiceDetailsFormStyles.css';

export const AgreementServiceDetailFormComponent = (
  props: ownFunctions.Props
) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  // Agreement Service Details data
  const [RequiresApproval, setRequiresApproval] = useState<boolean>(false);
  const [HasStop, setHasStop] = useState<boolean>(false);
  const [MaximumAllowed, setMaximumAllowed] = useState<number>();
  const [MaximumAllowedPerPeriod, setMaximumAllowedPerPeriod] =
    useState<number>();
  const [PeriodType, setPeriodType] = useState<number>();
  const [IsActive, setIsActive] = useState<boolean>(false);
  const [RequiresCopay, setRequiresCopay] = useState<boolean>(false);
  const [CopayComments, setCopayComments] = useState<string>();

  //useTranslation()
  const [t] = useTranslation();

  const periodOptions = ownFunctions.GetPeriodTypeOptions(t);

  // effects
  useEffect(() => {
    if (props.SelectedDetails) {
      setRequiresApproval(props.SelectedDetails.requiresApproval);
      setHasStop(props.SelectedDetails.hasStop);
      setMaximumAllowedPerPeriod(props.SelectedDetails.maximumAllowedPerPeriod);
      setMaximumAllowed(props.SelectedDetails.maximumAllowed);
      setPeriodType(props.SelectedDetails.periodType);
      setRequiresCopay(props.SelectedDetails.requiredCopago);
      setCopayComments(props.SelectedDetails.copagoComments);
      setIsActive(props.SelectedDetails.isActive);
    } else {
      ResetForm();
    }
  }, [props.SelectedDetails]);

  useEffect(() => {
    if (submittedForm && (props.SavingSuccess || props.UpdatingSuccess)) {
      ResetForm();
      props.dismissPanel();
    }
  }, [props.SavingSuccess, props.UpdatingSuccess]);

  const ResetForm = () => {
    setSubmittedForm(false);
    setFormValidationStatus(undefined);
    setRequiresApproval(false);
    setHasStop(false);
    setMaximumAllowed(undefined);
    setMaximumAllowedPerPeriod(undefined);
    setPeriodType(undefined);
    setRequiresCopay(false);
    setCopayComments(undefined);
    setIsActive(false);
  };

  // form validation

  const ValidateForm = (): boolean => {
    let validationResult: boolean = true;

    let FormValidation: FormValidation = { success: true, errors: [] };

    if (HasStop === undefined) {
      validationResult = false;
      FormValidation.errors.push(t('agreementServiceForm.parameters.hasStopError'));
    }

    if (
      HasStop &&
      MaximumAllowedPerPeriod === undefined &&
      MaximumAllowed === undefined
    ) {
      validationResult = false;
      FormValidation.errors.push(t('agreementServiceForm.parameters.hasStopErrorService'));
    }

    if (HasStop && PeriodType === undefined) {
      validationResult = false;
      // was 'parameter.hasStopErrorPeriod' — that path does not exist and the
      // raw key was rendered instead of the message
      FormValidation.errors.push(t('agreementServiceForm.parameters.hasStopErrorPeriod'));
    }

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
  };

  // events

  const HandleSubmitForm = () => {
    setSubmittedForm(true);
    if (ValidateForm()) {
      let CurrentValues: AgreementServicesDetail | NewAgreementServiceDetail;
      if (props.SelectedDetails) {
        CurrentValues = {
          agreementServiceId: props.SelectedDetails.agreementServiceId,
          id: props.SelectedDetails.id,
          insurancePlanReferenceName:
            props.SelectedDetails.insurancePlanReferenceName,
          insurancePlanId: props.SelectedDetails.insurancePlanId,
          requiresApproval: RequiresApproval,
          hasStop: HasStop,
          maximumAllowed: MaximumAllowed,
          maximumAllowedPerPeriod: MaximumAllowedPerPeriod,
          periodType: PeriodType,
          requiredCopago: RequiresCopay,
          copagoComments: CopayComments,
          isActive: IsActive
        };
        props.UpdateAgreementServiceDetail(CurrentValues);
      } else {
        CurrentValues = {
          agreementServiceId: props.AgreementService!.id,
          insurancePlanId: props.insurancePlanId!,
          insurancePlanReferenceName: props.insurancePlanReferenceName!,
          requiresApproval: RequiresApproval,
          hasStop: HasStop,
          maximumAllowed: MaximumAllowed,
          maximumAllowedPerPeriod: MaximumAllowedPerPeriod,
          periodType: PeriodType,
          requiredCopago: RequiresCopay,
          copagoComments: CopayComments,
          isActive: IsActive
        };
        props.AddAgreementServiceDetail(
          CurrentValues as NewAgreementServiceDetail
        );
      }
    }
  };

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const isBusy = props.isSaving || props.isUpdating;
  const showValidationErrors =
    submittedForm && FormValidationStatus && !FormValidationStatus.success;

  return (
    <OverlayDrawer
      open={props.isOpen}
      position="end"
      size="medium"
      onOpenChange={(_, data) => { if (!data.open) props.dismissPanel(); }}
      className="asd-drawer"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('agreementServiceForm.footer.cancelButton')}
              icon={<Dismiss24Regular />}
              onClick={HandleCancelForm}
            />
          }
        >
          {t('agreementServiceForm.footer.informationCover')}
        </DrawerHeaderTitle>

        {/* context: which plan and which service this coverage belongs to */}
        <div className="asd-context">
          <Badge appearance="tint" color="brand" shape="rounded">
            {props.insurancePlanReferenceName}
          </Badge>
          <span className="asd-context__service">
            {props.AgreementService?.medicalService?.serviceNameReference}
          </span>
          {props.AgreementService?.medicalService?.comments && (
            <span className="asd-context__comments">
              {props.AgreementService.medicalService.comments}
            </span>
          )}
        </div>
      </DrawerHeader>

      <DrawerBody className="asd-body">
        {showValidationErrors && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t('agreementServiceForm.footer.checkInformation')}
              </MessageBarTitle>
              <ul className="asd-errors">
                {FormValidationStatus?.errors.map((Error: string, index: number) => (
                  <li key={index}>{Error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {(props.failOnSaving || props.failOnUpdating) && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t('agreementServiceForm.footer.errorRec')}
              </MessageBarTitle>
              {props.error?.ErrorMessage}
              {props.error?.Errors && props.error.Errors.length > 0 && (
                <ul className="asd-errors">
                  {props.error.Errors.map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* ── Authorization ── */}
        <section className="asd-section">
          <h4 className="asd-section__title">
            {t('agreementServiceForm.sections.authorization')}
          </h4>
          <Switch
            label={t('agreementServiceForm.formFields.requiredAutorization')}
            checked={RequiresApproval}
            onChange={(_, data) => setRequiresApproval(data.checked)}
          />
        </section>

        <Divider />

        {/* ── Coverage cap ── */}
        <section className="asd-section">
          <h4 className="asd-section__title">
            {t('agreementServiceForm.sections.cap')}
          </h4>
          <Switch
            label={t('agreementServiceForm.formFields.stopCoverage')}
            checked={HasStop}
            onChange={(_, data) => setHasStop(data.checked)}
          />

          {HasStop && (
            <div className="asd-fields">
              <Field
                label={t('agreementServiceForm.textFields.maxAmount')}
                required
                validationState={
                  submittedForm && MaximumAllowed === undefined ? 'error' : 'none'
                }
                validationMessage={
                  submittedForm && MaximumAllowed === undefined
                    ? t('agreementServiceForm.textFields.requiredField')
                    : undefined
                }
              >
                <Input
                  type="number"
                  name="txt_MaximumAllowed"
                  autoComplete="off"
                  value={MaximumAllowed?.toString() || ''}
                  onChange={(_, data) =>
                    setMaximumAllowed(data.value ? Number(data.value) : undefined)
                  }
                />
              </Field>

              <Field
                label={t('agreementServiceForm.textFields.amountPeriod')}
                required
                validationState={
                  submittedForm && MaximumAllowedPerPeriod === undefined
                    ? 'error'
                    : 'none'
                }
                validationMessage={
                  submittedForm && MaximumAllowedPerPeriod === undefined
                    ? t('agreementServiceForm.textFields.requiredField')
                    : undefined
                }
              >
                <Input
                  type="number"
                  name="txt_MaximumAllowedPerPeriod"
                  autoComplete="off"
                  value={MaximumAllowedPerPeriod?.toString() || ''}
                  onChange={(_, data) =>
                    setMaximumAllowedPerPeriod(
                      data.value ? Number(data.value) : undefined
                    )
                  }
                />
              </Field>

              <Field
                label={t('agreementServiceForm.dropdown.coveragePeriod')}
                required
                validationState={
                  submittedForm && PeriodType === undefined ? 'error' : 'none'
                }
                validationMessage={
                  submittedForm && PeriodType === undefined
                    ? t('agreementServiceForm.textFields.requiredField')
                    : undefined
                }
              >
                <Dropdown
                  placeholder={t('agreementServiceForm.dropdown.typePeriod')}
                  value={
                    periodOptions.find((option) => option.key === PeriodType)?.text || ''
                  }
                  selectedOptions={PeriodType !== undefined ? [String(PeriodType)] : []}
                  onOptionSelect={(_, data) => setPeriodType(Number(data.optionValue))}
                >
                  {periodOptions.map((option) => (
                    <Option key={option.key} value={String(option.key)} text={option.text}>
                      {option.text}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          )}
        </section>

        <Divider />

        {/* ── Copay ── */}
        <section className="asd-section">
          <h4 className="asd-section__title">
            {t('agreementServiceForm.sections.copay')}
          </h4>
          <Switch
            label={t('agreementServiceForm.requires.copayRequires')}
            checked={RequiresCopay}
            onChange={(_, data) => setRequiresCopay(data.checked)}
          />

          {RequiresCopay && (
            <Field
              label={t('agreementServiceForm.requiresCopay.comments')}
              hint={t('agreementServiceForm.requiresCopay.description')}
            >
              <Textarea
                name="txt_CopayComments"
                resize="vertical"
                rows={4}
                value={CopayComments || ''}
                onChange={(_, data) => setCopayComments(data.value)}
              />
            </Field>
          )}
        </section>

        <Divider />

        {/* ── Coverage status ── */}
        <section className="asd-section">
          <h4 className="asd-section__title">
            {t('agreementServiceForm.coverStatus.state')}
          </h4>
          <Switch
            label={
              IsActive
                ? t('agreementServiceForm.coverStatus.stateOn')
                : t('agreementServiceForm.coverStatus.stateOff')
            }
            checked={IsActive}
            onChange={(_, data) => setIsActive(data.checked)}
          />
        </section>
      </DrawerBody>

      <DrawerFooter className="asd-footer">
        <Button appearance="secondary" onClick={HandleCancelForm} disabled={isBusy}>
          {t('agreementServiceForm.footer.cancelButton')}
        </Button>
        <Button
          appearance="primary"
          onClick={HandleSubmitForm}
          disabled={isBusy}
          icon={isBusy ? <Spinner size="extra-tiny" /> : undefined}
        >
          {t('agreementServiceForm.footer.saveButton')}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(AgreementServiceDetailFormComponent as any);
